import { Badge } from '@amore/ui/components/badge'
import { Field } from '@amore/ui/components/field'
import { FormLayout } from '@amore/ui/components/form-layout'
import { Input } from '@amore/ui/components/input'
import { Select, SelectItem } from '@amore/ui/components/select'
import { useRef, useState } from 'react'
import { newId } from '@/lib/format'
import { supplierStatusLabels, supplierStatusTone } from '@/lib/labels'
import { usePrototype } from '@/mock/store'
import { DetailSheet } from '@/patterns/dialog'
import { useToast } from '@/shell/toaster'
import { ceremonyBasics, saveCeremonyBasics } from './basics-model'
import { CeremonyFieldLabel, editorSections, softControl } from './editor-fields'

export function CeremonyPlaceSheet({ onClose }: { onClose: () => void }) {
  const { data, canEdit, update } = usePrototype()
  const { showToast } = useToast()
  const initial = ceremonyBasics(data)
  const [venueId, setVenueId] = useState(initial.venueSupplierId)
  const [location, setLocation] = useState(initial.location)
  const form = useRef<HTMLFormElement>(null)
  const venues = data.suppliers.filter(
    (supplier) =>
      supplier.category === 'venue' && (supplier.status !== 'cancelled' || supplier.id === venueId),
  )
  const selected = venues.find((venue) => venue.id === venueId)
  const dirty = canEdit && (venueId !== initial.venueSupplierId || location !== initial.location)
  return (
    <DetailSheet
      open
      eyebrow="Místo a příprava"
      title="Místo obřadu"
      dirty={dirty}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      primaryAction={
        canEdit ? { label: 'Uložit', onClick: () => form.current?.requestSubmit() } : undefined
      }
    >
      <FormLayout
        ref={form}
        className={editorSections}
        onSubmit={(event) => {
          event.preventDefault()
          if (!canEdit) return
          const eventId = newId('ceremony')
          update((current) =>
            saveCeremonyBasics(
              current,
              { ...ceremonyBasics(current), venueSupplierId: venueId, location },
              eventId,
            ),
          )
          showToast({ title: 'Místo obřadu uloženo', tone: 'success' })
          onClose()
        }}
      >
        <Field
          controlId="ceremony-place-supplier"
          label={<CeremonyFieldLabel>Místo z Dodavatelů</CeremonyFieldLabel>}
          description={
            selected ? (
              <Badge tone={supplierStatusTone(selected.status)}>
                {supplierStatusLabels[selected.status]}
              </Badge>
            ) : undefined
          }
        >
          <Select
            name="venue"
            value={venueId ?? 'none'}
            onValueChange={(value) => setVenueId(value === 'none' ? null : value)}
            disabled={!canEdit}
            variant="soft"
            className={softControl}
          >
            <SelectItem value="none">Zatím nevybráno</SelectItem>
            {venueId && !selected ? (
              <SelectItem value={venueId}>Dříve vybrané místo</SelectItem>
            ) : null}
            {venues.map((venue) => (
              <SelectItem key={venue.id} value={venue.id}>
                {venue.name}
              </SelectItem>
            ))}
          </Select>
        </Field>
        <Field
          controlId="ceremony-place-location"
          label={<CeremonyFieldLabel>Konkrétní prostor</CeremonyFieldLabel>}
          description="Zahrada, kaple nebo jiné místo, kde si řeknete ano."
        >
          <Input
            name="location"
            value={location}
            onChange={(event) => setLocation(event.target.value)}
            readOnly={!canEdit}
            maxLength={300}
            variant="soft"
            className={softControl}
            placeholder="Například zámecká zahrada"
          />
        </Field>
      </FormLayout>
    </DetailSheet>
  )
}
